import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  Copy,
  ExternalLink,
  Loader2,
  Package,
  Plug,
  RefreshCw,
  Settings2,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PageContainer, PageHeader, Card, Pill } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { aed } from "@/lib/format";

const ORDER_STATUSES = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "accepted", label: "Accepted" },
  { value: "preparing", label: "Preparing" },
  { value: "ready", label: "Ready" },
  { value: "picked_up", label: "Picked up" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_COLORS = {
  pending: "amber",
  accepted: "blue",
  preparing: "purple",
  ready: "green",
  picked_up: "slate",
  cancelled: "red",
  rejected: "red",
};

const NEXT_STATUS = {
  pending: "accepted",
  accepted: "preparing",
  preparing: "ready",
  ready: "picked_up",
};

function PlatformLogo({ setting, className = "h-10" }) {
  return (
    <img
      src={setting.logo_path}
      alt={setting.name}
      className={`${className} w-auto object-contain`}
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
    />
  );
}

function copyText(text, label = "Copied") {
  navigator.clipboard.writeText(text).then(() => toast.success(label));
}

export default function AggregatorHubPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const branchName = user?.default_branch?.name || "selected branch";

  const [tab, setTab] = useState("connections");
  const [editPlatform, setEditPlatform] = useState(null);
  const [orderFilter, setOrderFilter] = useState("all");
  const [mappingPlatformId, setMappingPlatformId] = useState("");
  const [mappingSku, setMappingSku] = useState("");
  const [mappingName, setMappingName] = useState("");
  const [mappingMenuItemId, setMappingMenuItemId] = useState("");
  const [reportFrom, setReportFrom] = useState("");
  const [reportTo, setReportTo] = useState("");
  const [reportPlatformId, setReportPlatformId] = useState("all");

  const [form, setForm] = useState({});

  const { data: settingsData, isLoading: settingsLoading } = useQuery({
    queryKey: ["aggregator-settings", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/aggregators/settings", {
        params: branchId ? { branch_id: branchId } : {},
      });
      return res.settings ?? [];
    },
    enabled: Boolean(user),
  });

  const settings = settingsData ?? [];

  const { data: countersData } = useQuery({
    queryKey: ["counters", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/counters", {
        params: branchId ? { branch_id: branchId } : {},
      });
      return res.counters ?? [];
    },
    enabled: Boolean(user),
  });
  const counters = countersData ?? [];

  const { data: menuItemsData } = useQuery({
    queryKey: ["menu-items", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/menu-items", {
        params: branchId ? { branch_id: branchId } : {},
      });
      return res.menu_items ?? res.data ?? [];
    },
    enabled: Boolean(user) && tab === "mapping",
  });
  const menuItems = menuItemsData ?? [];

  const activeMappingPlatformId = mappingPlatformId || settings[0]?.platform_id?.toString() || "";

  const { data: mappingsData, isLoading: mappingsLoading } = useQuery({
    queryKey: ["aggregator-mappings", branchId, activeMappingPlatformId],
    queryFn: async () => {
      const { data: res } = await api.get("/aggregators/mappings", {
        params: {
          branch_id: branchId,
          platform_id: activeMappingPlatformId,
        },
      });
      return res.mappings ?? [];
    },
    enabled: Boolean(user) && Boolean(activeMappingPlatformId) && tab === "mapping",
  });
  const mappings = mappingsData ?? [];

  const { data: ordersData, isLoading: ordersLoading, refetch: refetchOrders } = useQuery({
    queryKey: ["aggregator-orders", branchId, orderFilter],
    queryFn: async () => {
      const { data: res } = await api.get("/aggregators/orders", {
        params: { branch_id: branchId, status: orderFilter },
      });
      return res.orders ?? [];
    },
    enabled: Boolean(user) && tab === "orders",
    refetchInterval: tab === "orders" ? 30000 : false,
  });
  const orders = ordersData ?? [];

  const { data: reportData, refetch: refetchReport, isFetching: reportLoading } = useQuery({
    queryKey: ["aggregator-settlement", branchId, reportFrom, reportTo, reportPlatformId],
    queryFn: async () => {
      const params = { branch_id: branchId };
      if (reportFrom) params.date_from = reportFrom;
      if (reportTo) params.date_to = reportTo;
      if (reportPlatformId && reportPlatformId !== "all") {
        params.platform_id = reportPlatformId;
      }
      const { data: res } = await api.get("/aggregators/settlement-report", { params });
      return res;
    },
    enabled: Boolean(user) && tab === "settlement",
  });

  const saveSettingMutation = useMutation({
    mutationFn: async (payload) => {
      const { data: res } = await api.post("/aggregators/settings", {
        branch_id: branchId,
        ...payload,
      });
      return res;
    },
    onSuccess: (res) => {
      toast.success(res.message || "Settings saved.");
      queryClient.invalidateQueries({ queryKey: ["aggregator-settings", branchId] });
      setEditPlatform(null);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Could not save settings.");
    },
  });

  const saveMappingMutation = useMutation({
    mutationFn: async (payload) => {
      const { data: res } = await api.post("/aggregators/mappings", {
        branch_id: branchId,
        ...payload,
      });
      return res;
    },
    onSuccess: (res) => {
      toast.success(res.message || "Mapping saved.");
      queryClient.invalidateQueries({ queryKey: ["aggregator-mappings", branchId] });
      setMappingSku("");
      setMappingName("");
      setMappingMenuItemId("");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Could not save mapping.");
    },
  });

  const deleteMappingMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/aggregators/mappings/${id}`, {
        params: { branch_id: branchId },
      });
    },
    onSuccess: () => {
      toast.success("Mapping removed.");
      queryClient.invalidateQueries({ queryKey: ["aggregator-mappings", branchId] });
    },
    onError: () => toast.error("Could not remove mapping."),
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      const { data: res } = await api.patch(`/aggregators/orders/${id}`, {
        branch_id: branchId,
        status,
      });
      return res;
    },
    onSuccess: (res) => {
      toast.success(res.message || "Status updated.");
      queryClient.invalidateQueries({ queryKey: ["aggregator-orders", branchId] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Could not update status.");
    },
  });

  const enabledCount = useMemo(
    () => settings.filter((s) => s.is_enabled).length,
    [settings],
  );

  function openEdit(setting) {
    setEditPlatform(setting);
    setForm({
      platform_id: setting.platform_id,
      is_enabled: setting.is_enabled,
      merchant_id: setting.merchant_id || "",
      store_id: setting.store_id || "",
      api_key: "",
      webhook_secret: setting.webhook_secret || "",
      default_counter_id: setting.default_counter_id?.toString() || "",
      auto_accept: setting.auto_accept,
      auto_kot: setting.auto_kot,
      commission_percent: setting.commission_percent?.toString() || "0",
      notes: setting.notes || "",
    });
  }

  function saveEdit() {
    saveSettingMutation.mutate({
      platform_id: form.platform_id,
      is_enabled: form.is_enabled,
      merchant_id: form.merchant_id || null,
      store_id: form.store_id || null,
      api_key: form.api_key || undefined,
      webhook_secret: form.webhook_secret || undefined,
      default_counter_id: form.default_counter_id ? Number(form.default_counter_id) : null,
      auto_accept: form.auto_accept,
      auto_kot: form.auto_kot,
      commission_percent: Number(form.commission_percent) || 0,
      notes: form.notes || null,
    });
  }

  return (
    <PageContainer>
      <PageHeader
        title={t("aggregator.title", "Delivery Aggregators")}
        subtitle={t(
          "aggregator.subtitle",
          "Connect Talabat, Careem, Deliveroo, and Noon Food for {{branch}}.",
          { branch: branchName },
        )}
      />

      <div className="mb-4 flex flex-wrap gap-2 text-sm text-muted-foreground">
        <Pill tone={enabledCount > 0 ? "green" : "slate"}>
          {enabledCount} platform{enabledCount === 1 ? "" : "s"} connected
        </Pill>
        <span className="text-xs self-center">
          Brand logos use partner colours; replace SVGs in /aggregators/ with official assets from each platform&apos;s brand kit when available.
        </span>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex flex-wrap h-auto gap-1">
          <TabsTrigger value="connections" className="gap-1.5">
            <Plug className="h-4 w-4" />
            Connections
          </TabsTrigger>
          <TabsTrigger value="mapping" className="gap-1.5">
            <Settings2 className="h-4 w-4" />
            Menu mapping
          </TabsTrigger>
          <TabsTrigger value="orders" className="gap-1.5">
            <Package className="h-4 w-4" />
            Orders
          </TabsTrigger>
          <TabsTrigger value="settlement" className="gap-1.5">
            <CheckCircle2 className="h-4 w-4" />
            Settlement
          </TabsTrigger>
        </TabsList>

        <TabsContent value="connections">
          {settingsLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading platforms…
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {settings.map((setting) => (
                <Card key={setting.platform_id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="rounded-lg px-3 py-2 shrink-0"
                        style={{ backgroundColor: `${setting.brand_color}22` }}
                      >
                        <PlatformLogo setting={setting} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold">{setting.name}</h3>
                        <Pill tone={setting.is_enabled ? "green" : "slate"} className="mt-1">
                          {setting.is_enabled ? "Enabled" : "Not connected"}
                        </Pill>
                        {setting.is_enabled && setting.default_counter_name && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Counter: {setting.default_counter_name}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => openEdit(setting)}>
                      Configure
                    </Button>
                  </div>
                  {setting.slug === "talabat" && (
                    <p className="mt-3 text-xs text-muted-foreground border-t pt-3">
                      UAE pilot platform — configure Talabat first, then add others.
                    </p>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="mapping">
          <Card className="p-4 mb-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
              <div>
                <Label>Platform</Label>
                <Select
                  value={activeMappingPlatformId}
                  onValueChange={setMappingPlatformId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select platform" />
                  </SelectTrigger>
                  <SelectContent>
                    {settings.map((s) => (
                      <SelectItem key={s.platform_id} value={s.platform_id.toString()}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Platform SKU</Label>
                <Input
                  value={mappingSku}
                  onChange={(e) => setMappingSku(e.target.value)}
                  placeholder="e.g. TLB-001"
                />
              </div>
              <div>
                <Label>Platform item name</Label>
                <Input
                  value={mappingName}
                  onChange={(e) => setMappingName(e.target.value)}
                  placeholder="As shown on app"
                />
              </div>
              <div>
                <Label>ERP menu item</Label>
                <Select value={mappingMenuItemId} onValueChange={setMappingMenuItemId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select item" />
                  </SelectTrigger>
                  <SelectContent>
                    {menuItems.map((item) => (
                      <SelectItem key={item.id} value={item.id.toString()}>
                        {item.code} — {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  className="w-full"
                  disabled={
                    !mappingSku ||
                    !mappingMenuItemId ||
                    !activeMappingPlatformId ||
                    saveMappingMutation.isPending
                  }
                  onClick={() =>
                    saveMappingMutation.mutate({
                      platform_id: Number(activeMappingPlatformId),
                      platform_sku: mappingSku.trim(),
                      platform_item_name: mappingName || null,
                      menu_item_id: Number(mappingMenuItemId),
                      is_available: true,
                    })
                  }
                >
                  Add mapping
                </Button>
              </div>
            </div>
          </Card>

          {mappingsLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading mappings…
            </div>
          ) : mappings.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No mappings yet. Map each platform SKU to your menu item so webhook orders create correct KOT lines.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3">SKU</th>
                    <th className="text-left p-3">Platform name</th>
                    <th className="text-left p-3">Menu item</th>
                    <th className="text-right p-3 w-16" />
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((m) => (
                    <tr key={m.id} className="border-t">
                      <td className="p-3 font-mono text-xs">{m.platform_sku}</td>
                      <td className="p-3">{m.platform_item_name || "—"}</td>
                      <td className="p-3">
                        {m.menu_item_code ? `${m.menu_item_code} — ${m.menu_item_name}` : "—"}
                      </td>
                      <td className="p-3 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteMappingMutation.mutate(m.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="orders">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Select value={orderFilter} onValueChange={setOrderFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ORDER_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => refetchOrders()}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>

          {ordersLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading orders…
            </div>
          ) : orders.length === 0 ? (
            <p className="text-sm text-muted-foreground">No aggregator orders for this filter.</p>
          ) : (
            <div className="grid gap-3">
              {orders.map((order) => {
                const next = NEXT_STATUS[order.status];
                return (
                  <Card key={order.id} className="p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        {order.platform && (
                          <PlatformLogo setting={order.platform} className="h-8" />
                        )}
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold">#{order.platform_order_id}</span>
                            <Pill tone={STATUS_COLORS[order.status] || "slate"}>
                              {order.status.replace("_", " ")}
                            </Pill>
                          </div>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            {order.customer_name || "Customer"}
                            {order.customer_phone ? ` · ${order.customer_phone}` : ""}
                          </p>
                          {order.bill_number && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Bill {order.bill_number} · {aed(order.platform_total)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {next && (
                          <Button
                            size="sm"
                            onClick={() =>
                              statusMutation.mutate({ id: order.id, status: next })
                            }
                            disabled={statusMutation.isPending}
                          >
                            Mark {next.replace("_", " ")}
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="settlement">
          <Card className="p-4 mb-4">
            <div className="grid gap-4 md:grid-cols-4">
              <div>
                <Label>From</Label>
                <Input type="date" value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} />
              </div>
              <div>
                <Label>To</Label>
                <Input type="date" value={reportTo} onChange={(e) => setReportTo(e.target.value)} />
              </div>
              <div>
                <Label>Platform</Label>
                <Select value={reportPlatformId} onValueChange={setReportPlatformId}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All platforms</SelectItem>
                    {settings.map((s) => (
                      <SelectItem key={s.platform_id} value={s.platform_id.toString()}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button className="w-full" variant="outline" onClick={() => refetchReport()} disabled={reportLoading}>
                  {reportLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run report"}
                </Button>
              </div>
            </div>
          </Card>

          {reportData?.rows?.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3">Platform</th>
                    <th className="text-right p-3">Orders</th>
                    <th className="text-right p-3">Gross</th>
                    <th className="text-right p-3">Commission</th>
                    <th className="text-right p-3">Net settlement</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.rows.map((row) => (
                    <tr key={row.platform_id} className="border-t">
                      <td className="p-3 font-medium">{row.name}</td>
                      <td className="p-3 text-right">{row.order_count}</td>
                      <td className="p-3 text-right">{aed(row.gross_total)}</td>
                      <td className="p-3 text-right">{aed(row.commission_total)}</td>
                      <td className="p-3 text-right font-medium">{aed(row.net_settlement)}</td>
                    </tr>
                  ))}
                  <tr className="border-t bg-muted/30 font-semibold">
                    <td className="p-3">Total</td>
                    <td className="p-3 text-right">{reportData.totals.order_count}</td>
                    <td className="p-3 text-right">{aed(reportData.totals.gross_total)}</td>
                    <td className="p-3 text-right">{aed(reportData.totals.commission_total)}</td>
                    <td className="p-3 text-right">{aed(reportData.totals.net_settlement)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Run the report to see commission and net settlement by platform.
            </p>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={Boolean(editPlatform)} onOpenChange={(open) => !open && setEditPlatform(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editPlatform && <PlatformLogo setting={editPlatform} className="h-8" />}
              {editPlatform?.name} — connection
            </DialogTitle>
          </DialogHeader>

          {editPlatform && (
            <div className="grid gap-4 py-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="agg-enabled">Enable for this branch</Label>
                <Switch
                  id="agg-enabled"
                  checked={form.is_enabled}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, is_enabled: v }))}
                />
              </div>

              <div className="grid gap-2">
                <Label>Merchant ID</Label>
                <Input
                  value={form.merchant_id}
                  onChange={(e) => setForm((f) => ({ ...f, merchant_id: e.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label>Store / outlet ID</Label>
                <Input
                  value={form.store_id}
                  onChange={(e) => setForm((f) => ({ ...f, store_id: e.target.value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label>API key {editPlatform.has_api_key && "(leave blank to keep)"}</Label>
                <Input
                  type="password"
                  value={form.api_key}
                  onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))}
                  placeholder="From partner portal"
                />
              </div>
              <div className="grid gap-2">
                <Label>Default POS counter</Label>
                <Select
                  value={form.default_counter_id}
                  onValueChange={(v) => setForm((f) => ({ ...f, default_counter_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select counter" />
                  </SelectTrigger>
                  <SelectContent>
                    {counters.map((c) => (
                      <SelectItem key={c.id} value={c.id.toString()}>
                        {c.name} ({c.device_code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Commission %</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.commission_percent}
                  onChange={(e) => setForm((f) => ({ ...f, commission_percent: e.target.value }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Auto-accept orders</Label>
                <Switch
                  checked={form.auto_accept}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, auto_accept: v }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Auto-fire KOT on accept</Label>
                <Switch
                  checked={form.auto_kot}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, auto_kot: v }))}
                />
              </div>

              <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Webhook (give to platform)
                </Label>
                <div className="flex gap-2">
                  <Input readOnly value={editPlatform.webhook_url} className="text-xs font-mono" />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => copyText(editPlatform.webhook_url, "Webhook URL copied")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={form.webhook_secret}
                    onChange={(e) => setForm((f) => ({ ...f, webhook_secret: e.target.value }))}
                    className="text-xs font-mono"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => copyText(form.webhook_secret, "Secret copied")}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Send header <code className="bg-muted px-1 rounded">X-Webhook-Secret</code> with each POST.
                  Sample JSON: order_id, total, customer, items[].sku, items[].qty
                </p>
                <a
                  href="https://developer.talabat.com"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-primary"
                >
                  Partner docs <ExternalLink className="h-3 w-3" />
                </a>
              </div>

              <div className="grid gap-2">
                <Label>Notes</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPlatform(null)}>
              Cancel
            </Button>
            <Button onClick={saveEdit} disabled={saveSettingMutation.isPending}>
              {saveSettingMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
